import { Location } from '@angular/common'
import { Component, HostBinding, Injectable, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { every } from 'lodash-es'
import { LazyLoadEvent } from 'primeng/api'
import { resourceUsage } from 'process'
import { BehaviorSubject, Subject, combineLatest, forkJoin } from 'rxjs'
import { debounceTime, filter, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { AppPractice, PIMSProduct, ProductType, ProductType2, ProductsClient, VetCorProduct } from 'src/app/shared/services/ppv.api'
import { ProductsService } from '../products.service'
import { ComponentStore } from '@ngrx/component-store'
import { PIMSProductUIModel } from '../models/models'
import { CheckboxChangeEvent } from 'primeng/checkbox'
import { ProductFunctions } from '../functions'

type ProductTypeEnum = {
   productTypeSKey: number
   productTypeName: string
}

@Injectable()
export class VCProductsListComponentStore extends ComponentStore<{
   selectedVCProductSKey?: number
   productTypeSKey?: number
   loadingPIMS: boolean
   pimsRows?: PIMSProductUIModel[]
   missingPracticesVisible: boolean
   searchText: string
   searchCode: string
   selectedCategory: string
}> {
   constructor() {
      super({
         loadingPIMS: true,
         missingPracticesVisible: false,
         searchText: '',
         searchCode: '',
         selectedCategory: '',
      })
   }
}

@Component({
   selector: 'vc-products-vc-list',
   templateUrl: './products-vc-list.component.html',
   styleUrls: ['./products-vc-list.component.scss'],
   providers: [VCProductsListComponentStore],
   standalone: false,
})
export class ProductsVcListComponent implements OnInit {
   loading = true
   rows: VetCorProduct[] = []
   rowsToTake = 100
   practicesLookup$ = new BehaviorSubject<{
      [practiceSKey: number]: AppPractice
   }>(undefined)
   practices: AppPractice[] = []

   searchTextChanged$ = new Subject<string>()
   searchTextInput = ''
   selectedCategory = ''
   yourCatList: any[] = []
   searchCodeInput = ''
   searchCodeChanged$ = new Subject<string>()

   loading$ = this.productsService.loading$
   productTypes: ProductTypeEnum[] = Object.values(ProductType)
      .filter((p) => typeof p === 'string')
      .map(
         (p) =>
            <ProductTypeEnum>{
               productTypeSKey: ProductType[p],
               productTypeName: p,
            }
      )

   selectedProduct$ = combineLatest([this.store.select((s) => s.selectedVCProductSKey), this.productsService.allVCProducts$]).pipe(
      map(([selectedProductSKey, allProducts]) => {
         let foundProduct: VetCorProduct = undefined
         allProducts?.filter((p) => p.vcProductSKey === selectedProductSKey).forEach((p) => (foundProduct = p))
         return foundProduct
      })
   )

   state$ = combineLatest([this.store.select((s) => s), this.selectedProduct$]).pipe(
      map(([state, selectedProduct]) => ({
         ...state,
         selectedProduct,
      }))
   )

   hierarchicalProducts$ = this.productService.hierarchicalProducts$

   selectedVcProductSKey: number

   @HostBinding('class.full-flex-height') fullFlexHeight = true

   constructor(
      private api: ProductsClient,
      private productService: ProductsService,
      private authManager: AuthManager,
      private route: ActivatedRoute,
      private location: Location,
      private productsService: ProductsService,
      private store: VCProductsListComponentStore
   ) {}

   ngOnInit(): void {
      const vcProductSKeyParam = this.route.snapshot.paramMap.get('vcProductSKey')

      this.selectedVcProductSKey = !!vcProductSKeyParam ? Number.parseInt(vcProductSKeyParam) : undefined

      this.store.setState({
         selectedVCProductSKey: this.selectedVcProductSKey,
         loadingPIMS: false,
         missingPracticesVisible: false,
         searchText: '',
         searchCode: '',
         selectedCategory: '',
      })

      this.searchTextChanged$.pipe(debounceTime(300)).subscribe((searchText) => {
         this.store.patchState({
            searchText: searchText,
         })
      })

      this.searchCodeChanged$.pipe(debounceTime(300)).subscribe((searchCode) => {
         if (searchCode) {
            this.store.setState((state) => ({
               ...state,
               pimsRows: state.pimsRows?.filter((row) => row.code.toLowerCase().includes(searchCode.toLowerCase())),
            }))
         }

         this.store.patchState({
            searchCode: searchCode,
         })
      })

      this.productsService.loadVCProducts()

      this.authManager
         .getCurrentUser()
         .pipe(
            take(1),
            tap((user) => {
               this.practices = user.practices
               const newLookup = {}
               this.practices.forEach((prac) => {
                  newLookup[prac.id] = prac
               })
               this.practicesLookup$.next(newLookup)
            })
         )
         .subscribe()

      const pimsProductResults$ = combineLatest([this.store.select((s) => s.selectedVCProductSKey)]).pipe(
         tap(([prod]) => {
            if (!prod)
               this.store.patchState({
                  pimsRows: undefined,
               })
         }),
         filter(([prod]) => !!prod),
         tap(([prod]) => {
            this.store.patchState({
               loadingPIMS: true,
            })
         }),
         switchMap(([vcProductSKey]) =>
            this.api.searchPIMSProducts({
               vcProductSKey,
               practiceSKey: undefined,
            })
         )
      )

      combineLatest([
         pimsProductResults$,
         this.practicesLookup$,
         this.store.select((s) => s.productTypeSKey),
         this.store.select((s) => s.missingPracticesVisible),
         this.store.select((s) => s.searchText),
         this.store.select((s) => s.searchCode),
         this.store.select((s) => s.selectedCategory),
         this.productService.allCategories$,
         this.productService.allVCProducts$,
      ])
         .pipe(
            filter(([r, p]) => !!p),
            map(([rows, practices, productType, missingPracticesVisible, searchText, searchCode, selectedCategory, vcCategories, allProducts]) => {
               const categoryLookup = {}
               vcCategories.forEach((cat) => (categoryLookup[cat.vcProductCategorySKey] = cat))

               this.yourCatList = Array.from(new Set(rows.map((req) => req.categoryName || ''))).sort((a, b) => (a || '').localeCompare(b || ''))
               const filteredRows = rows.filter((row) => {
                  let isValid = true
                  if (productType > 0) isValid = isValid && row.productType === productType

                  if (searchText && !!row.name) {
                     isValid = isValid && row.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1
                  }

                  if (this.selectedCategory) {
                     isValid = isValid && row.categoryName === this.selectedCategory
                  }

                  if (this.searchCodeInput && !!row.code) {
                     isValid = isValid && row.code.toLowerCase().indexOf(this.searchCodeInput.toLowerCase()) > -1
                  }

                  return isValid
               })

               filteredRows.forEach((row) => {
                  row.practice = practices[row.practiceSKey]
                  if (row.vcProduct?.vcProductCategorySKey) row.vcProduct.vcProductCategory = categoryLookup[row.vcProduct?.vcProductCategorySKey]
               })

               if (missingPracticesVisible) {
                  const missingPractices = {}
                  Object.keys(practices).forEach((p) => (missingPractices[p] = true))

                  filteredRows.forEach((p) => {
                     if (missingPractices[p?.practiceSKey]) missingPractices[p.practiceSKey] = false
                  })

                  const onlyMissing = []

                  Object.keys(missingPractices).forEach((pid) => {
                     if (missingPractices[pid]) {
                        var prod = {
                           ...allProducts.find((p) => p.vcProductSKey == this.selectedVcProductSKey),
                        }
                        prod.vcProductCategory = categoryLookup[prod.vcProductCategorySKey]
                        onlyMissing.push({
                           practice: practices[pid],
                           practiceSKey: +pid,
                           productSKey: undefined,
                           vcProduct: prod,
                           vcProductSKey: prod.vcProductSKey,
                           name: '',
                           code: '',
                           missing: true,
                        } as any)
                     }
                  })

                  return onlyMissing
               }

               return filteredRows
            }),
            tap((res) => {
               this.store.patchState({
                  pimsRows: res.sort((a, b) => a.practice?.nameId?.localeCompare(b.practice?.nameId)),
                  loadingPIMS: false,
               })
            })
         )
         .subscribe()
   }

   onSearch() {
      this.searchTextChanged$.next(this.searchTextInput)
   }

   loadMore($event: LazyLoadEvent) {}

   onProductTypeChanged(product?: ProductTypeEnum) {
      this.store.patchState({
         productTypeSKey: product?.productTypeSKey,
      })
   }

   onCategoryChanged(selectedCategory: string) {
      if (selectedCategory) {
         this.store.setState((state) => ({
            ...state,
            pimsRows: state.pimsRows?.filter((row) => row.categoryName === selectedCategory),
         }))

         this.selectedCategory = selectedCategory
      }

      this.store.patchState({
         selectedCategory: selectedCategory,
      })
   }

   onSearchCode() {
      this.searchCodeChanged$.next(this.searchCodeInput) // called on every input change
   }

   onCategoryClear() {
      this.selectedCategory = '' // or null, depending on your logic
      this.store.patchState((state) => ({
         ...state,
         selectedCategory: this.selectedCategory || '',
      }))
   }

   onVCProductSelected(vcProductSKey: number) {
      if (!!vcProductSKey) {
         this.location.go('/products/standard/' + vcProductSKey)
      } else {
         this.location.go('/products/standard')
      }

      this.selectedVcProductSKey = vcProductSKey

      this.store.patchState({
         selectedVCProductSKey: vcProductSKey,
      })
   }

   setMissingPracticesVisible($event: CheckboxChangeEvent) {
      this.store.patchState({
         missingPracticesVisible: $event.checked,
      })
   }
}
